import React, { useState, useEffect, useCallback } from "react";
import { ActionIcon, Button, Popover, Stack, Box, Text, Group, ScrollArea, Checkbox, TextInput } from "@mantine/core";
import { MdPhoneIphone, MdClear, MdSearch } from "react-icons/md";
import { usePopoverManager } from "./PopoverManager";

interface TouchpointFilterControlProps {
    value: string[]; // Selected touchpoints
    onChange: (touchpoints: string[]) => void;
    availableTouchpoints: string[]; // All available touchpoints from data
    disabled?: boolean;
    loading?: boolean;
    maxVisibleItems?: number; // Maximum number of items to show before scrolling (default: 6)
}

export const TouchpointFilterControl: React.FC<TouchpointFilterControlProps> = ({
    value,
    onChange,
    availableTouchpoints,
    disabled = false,
    loading = false,
    maxVisibleItems = 6
}) => {
    const { isPopoverOpen, togglePopover, setOpenPopover } = usePopoverManager();
    const [tempSelection, setTempSelection] = useState<string[]>(value);
    const [searchTerm, setSearchTerm] = useState("");

    const popoverId = "touchpoint-filter";
    const opened = isPopoverOpen(popoverId);

    // Update temp selection when value changes from parent
    useEffect(() => {
        setTempSelection(value);
    }, [value]);

    // Reset search term when popover opens
    useEffect(() => {
        if (opened) {
            setSearchTerm("");
        }
    }, [opened]);

    const handleToggle = () => {
        const wasOpened = opened;
        togglePopover(popoverId);

        if (!wasOpened) {
            // Reset temp selection to current value when opening
            setTempSelection(value);
            setSearchTerm("");
        }
    };

    const handleTouchpointToggle = (touchpoint: string) => {
        setTempSelection(prev => {
            if (prev.includes(touchpoint)) {
                return prev.filter(t => t !== touchpoint);
            } else {
                return [...prev, touchpoint];
            }
        });
    };

    const handleSelectAll = () => {
        setTempSelection([...availableTouchpoints]);
    };

    const handleClearAll = () => {
        setTempSelection([]);
    };

    const handleApply = () => {
        onChange(tempSelection);
        setOpenPopover(null);
    };

    const handleCancel = () => {
        setTempSelection(value);
        setOpenPopover(null);
    };

    const handleClose = () => {
        // Only close if this popover is actually the one that's open
        if (opened) {
            handleCancel();
        }
    };

    // Check if there are pending changes
    const hasPendingChanges =
        tempSelection.length !== value.length ||
        !tempSelection.every(touchpoint => value.includes(touchpoint)) ||
        !value.every(touchpoint => tempSelection.includes(touchpoint));

    // Always show the filter as having no active filters when no touchpoints are available
    // or when all available touchpoints are selected (which means "show all")
    const hasFilters = availableTouchpoints.length > 0 && value.length > 0 && value.length < availableTouchpoints.length;

    const displayText =
        availableTouchpoints.length === 0
            ? "All Touchpoints" // No touchpoints available, show all legacy + new data
            : value.length === 0 || value.length === availableTouchpoints.length
              ? "All Touchpoints"
              : `${value.length}`;

    // Filter touchpoints based on search term
    const filteredTouchpoints = availableTouchpoints.filter(touchpoint => touchpoint.toLowerCase().includes(searchTerm.toLowerCase()));

    // Calculate dynamic height based on number of items to show
    const itemsToShow = filteredTouchpoints.length === 0 ? 1 : Math.min(filteredTouchpoints.length, maxVisibleItems);
    const itemHeight = 32; // Approximate height per checkbox item (including gap)
    const minHeight = 40; // Minimum height for empty state or single item
    const dynamicHeight = Math.max(minHeight, itemsToShow * itemHeight);

    return (
        <Popover
            opened={opened}
            withArrow={false}
            closeOnClickOutside={false}
            closeOnEscape={true}
            trapFocus={false}
            withinPortal={true}
            position="bottom-start"
            offset={4}
            onClose={handleClose}
            styles={{
                dropdown: {
                    padding: "12px",
                    minWidth: "320px",
                    maxWidth: "400px",
                    border: "2px solid #339af0",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(51, 154, 240, 0.15)",
                    backgroundColor: "#ffffff"
                }
            }}
        >
            <Popover.Target>
                <ActionIcon
                    variant={hasFilters ? "filled" : "light"}
                    size="sm"
                    onClick={handleToggle}
                    disabled={disabled || loading}
                    color={hasFilters ? "blue" : undefined}
                    title={displayText}
                >
                    <MdPhoneIphone size={16} />
                </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap="sm">
                    {availableTouchpoints.length > 0 ? (
                        <>
                            {/* Search Input */}
                            <TextInput
                                placeholder="Search touchpoints..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                leftSection={<MdSearch size={16} />}
                                size="sm"
                                styles={{
                                    input: {
                                        border: "1px solid #e9ecef",
                                        backgroundColor: "#f8f9fa",
                                        borderRadius: "6px",
                                        "&:focus": {
                                            borderColor: "#339af0",
                                            backgroundColor: "#ffffff"
                                        }
                                    }
                                }}
                            />

                            {/* Select All / Clear All buttons */}
                            <Group gap="xs">
                                <Button size="xs" variant="subtle" onClick={handleSelectAll} disabled={tempSelection.length === availableTouchpoints.length}>
                                    Select All
                                </Button>
                                <Button size="xs" variant="subtle" onClick={handleClearAll} disabled={tempSelection.length === 0}>
                                    Clear All
                                </Button>
                            </Group>

                            {/* Touchpoint List */}
                            <ScrollArea h={dynamicHeight} offsetScrollbars>
                                <Stack gap="xs">
                                    {filteredTouchpoints.length > 0 ? (
                                        filteredTouchpoints.map(touchpoint => (
                                            <Box key={touchpoint}>
                                                <Checkbox
                                                    label={touchpoint || "Unknown"}
                                                    checked={tempSelection.includes(touchpoint)}
                                                    onChange={() => handleTouchpointToggle(touchpoint)}
                                                    size="sm"
                                                    styles={{
                                                        label: {
                                                            fontSize: "14px",
                                                            cursor: "pointer"
                                                        },
                                                        input: {
                                                            cursor: "pointer"
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        ))
                                    ) : (
                                        <Text size="sm" c="dimmed" ta="center" py="md">
                                            No touchpoints found matching "{searchTerm}"
                                        </Text>
                                    )}
                                </Stack>
                            </ScrollArea>

                            {/* Apply/Cancel buttons - always visible */}
                            <Group gap="xs" grow>
                                <Button size="xs" variant="filled" onClick={handleApply} color="blue" disabled={!hasPendingChanges}>
                                    Apply Filter
                                </Button>
                                <Button size="xs" variant="subtle" onClick={handleCancel}>
                                    Cancel
                                </Button>
                            </Group>
                        </>
                    ) : (
                        <Text size="sm" c="dimmed" ta="center" py="md">
                            No touchpoint data available.
                            <br />
                            Showing all records (including legacy data without touchpoint information).
                        </Text>
                    )}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
};
